import React, { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Box, Button, Loader, Flex, Typography } from "@strapi/design-system"
import { mount, stopWebcam, takePhoto, unmount } from "../../services/player/player"
import "./AddCustomImageModalInViewer.css"
import { useAppDispatch } from "hooks/storeHooks"
import { setCustomPhoto } from "store/user/userSlice"

interface AddCustomImageModalInViewerProps {
  open: boolean
  onClose: () => void
  onSave: (data: string | Blob) => void
  size?: number
}

export const AddCustomImageModalInViewer: React.FC<AddCustomImageModalInViewerProps> = ({
  open,
  onClose,
  onSave,
  size = 500,
}) => {
  type Mode = "initial" | "file" | "webcam" | "preview"
  const [mode, setMode] = useState<Mode>("initial")
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null)
  const hiddenFileInput = useRef(null)

  const [loading, setLoading] = useState<boolean>(false)
  const [ready, setReady] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const initWebcam = async () => {
    setLoading(true)
    setReady(false)
    try {
      mount("#webar")

      setReady(true)
    } catch (err) {
      console.error("Ошибка инициализации Banuba Player:", err)
    } finally {
      setLoading(false)
    }
  }

  const resetPlayerAndWebcam = useCallback(async () => {
    if (mode === "webcam" || mode === "preview") {
      stopWebcam()
      unmount("#webar")
    }
  }, [mode])

  useEffect(() => {
    if (mode === "webcam") initWebcam()
  }, [mode])

  useEffect(() => {
    return () => {
      resetPlayerAndWebcam()
    }
  }, [resetPlayerAndWebcam])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const imageURL = URL.createObjectURL(e.target.files[0])
      onSave(imageURL)
      onClose()
    }
  }

  const handleConfirmPhoto = () => {
    if (selectedPhoto) {
      dispatch(setCustomPhoto(selectedPhoto))
      setSelectedPhoto(null)
      onSave(selectedPhoto)
      onClose()
    }
  }

  const handleCancelPhoto = () => {
    setSelectedPhoto(null)
    setMode("webcam")
  }

  if (!open) return null

  return (
    <>
      <Box
        style={{
          position: "relative",
          width: size,
          height: size,
          background: "#fff",
          borderRadius: 8,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "initial" && (
          <Box
            style={{
              width: size,
              height: size,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "35px",
              backgroundColor: "#F6F6F9",
            }}
          >
            <Box
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #DCDCE3",
                borderRadius: "4px",
              }}
            >
              <Box
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="epsilon"
                  style={{
                    fontWeight: "600",
                    fontSize: "15px",
                    lineHeight: "15px",
                    color: "#000000",
                  }}
                >
                  {t("addCustomImageModal.instructions")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 24,
                    margin: "30px 0",
                    width: "100%",
                    padding: "0 15px",
                  }}
                >
                  <Flex alignItems="center" justifyContent="center" direction="column" flex={1}>
                    <img src="/face-uncovered.svg" alt="Face Uncovered" width={40} height={40} />
                    <Typography
                      variant="omega"
                      style={{
                        color: "#666684",
                      }}
                    >
                      {t("addCustomImageModal.faceUncovered")}
                    </Typography>
                  </Flex>
                  <Flex alignItems="center" justifyContent="center" direction="column" flex={1}>
                    <img src="/no-glasses.svg" alt="No Glasses" width={40} height={40} />
                    <Typography
                      variant="omega"
                      style={{
                        color: "#666684",
                      }}
                    >
                      {t("addCustomImageModal.noGlasses")}
                    </Typography>
                  </Flex>
                  <Flex alignItems="center" justifyContent="center" direction="column" flex={1}>
                    <img src="/even-light.svg" alt="Even Lighting" width={40} height={40} />
                    <Typography
                      variant="omega"
                      style={{
                        color: "#666684",
                      }}
                    >
                      {t("addCustomImageModal.evenLight")}
                    </Typography>
                  </Flex>
                </Box>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    width: "55%",
                    marginTop: "10px",
                  }}
                >
                  <Button
                    fullWidth
                    onClick={() => {
                      hiddenFileInput.current?.click()
                    }}
                  >
                    <input
                      ref={hiddenFileInput}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      multiple={false}
                      hidden
                    />
                    {t("addCustomImageModal.selectImage")}
                  </Button>
                  <Button fullWidth variant="tertiary" onClick={() => setMode("webcam")}>
                    {t("addCustomImageModal.takePhoto")}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {mode === "webcam" && !selectedPhoto && (
          <>
            {loading || !ready ? (
              <Box
                style={{
                  position: "absolute",
                  zIndex: 2,
                  top: "45%",
                  right: "45%",
                }}
              >
                <Loader />
              </Box>
            ) : null}
            <Box
              id="webar"
              style={{
                height: loading || !ready ? "0px" : "100%",
                width: "100%",
                background: "#000",
                borderRadius: 8,
                overflow: "hidden",
              }}
            />
            <Button
              style={{
                position: "absolute",
                right: "45%",
                bottom: "20px",
                width: "45px",
                height: "45px",
                margin: "3px",
                borderRadius: "50%",
                outline: "3px solid white",
                backgroundColor: "white",
                border: "none",
                outlineOffset: "3.5px",
                cursor: "pointer",
              }}
              onClick={async () => {
                const photoBlob: Blob = await takePhoto()
                const photoURL = URL.createObjectURL(photoBlob)
                setSelectedPhoto(photoURL)
                setMode("preview")
              }}
            />
          </>
        )}

        {mode === "preview" && selectedPhoto && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <img
              src={selectedPhoto}
              alt="Captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
            <Box
              style={{
                position: "absolute",
                bottom: "60px",
                height: "45px",
                margin: "3px",
                width: "40%",
              }}
            >
              <Box style={{ display: "flex", gap: 8, marginTop: 16, flexDirection: "column" }}>
                <Button fullWidth onClick={handleConfirmPhoto}>
                  {t("addCustomImageModal.continue")}
                </Button>
                <Button fullWidth variant="tertiary" onClick={handleCancelPhoto}>
                  {t("addCustomImageModal.takeAnother")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}
